import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';

interface Integration {
  id: number;
  user_id: string;
  integration_type: string;
  is_active: boolean;
  config: any;
  created_at: string;
  updated_at: string;
}

interface IntegrationConfig {
  name: string;
  type: string;
  description: string;
  icon: string;
  instructions: string;
  fields: {
    name: string;
    label: string;
    type: string;
    placeholder: string;
    required: boolean;
    helpText?: string;
  }[];
  tier?: string; // 'free', 'basic', 'premium'
  testable?: boolean;
}

const Integrations: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showToast } = useToast();

  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [loading, setLoading] = useState(true);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState<IntegrationConfig | null>(null);
  const [formData, setFormData] = useState<any>({});

  const [testing, setTesting] = useState(false);

  const availableIntegrations: IntegrationConfig[] = [
    {
      name: 'Zapier',
      type: 'zapier',
      description: 'Connect to 5000+ apps with automated workflows',
      icon: '⚡',
      instructions: 'Create a Zap in Zapier with "Webhooks by Zapier" as the trigger. Select "Catch Hook" and copy the webhook URL here.',
      testable: true,
      fields: [
        {
          name: 'webhook_url',
          label: 'Webhook URL',
          type: 'url',
          placeholder: 'https://hooks.zapier.com/hooks/catch/...',
          required: true,
          helpText: 'Go to zapier.com → Create Zap → Trigger: Webhooks → Catch Hook → Copy URL'
        }
      ]
    },
    {
      name: 'Mailchimp',
      type: 'mailchimp',
      description: 'Sync course subscribers to your email lists',
      icon: '📧',
      instructions: 'Find your API key in Mailchimp under Account → Extras → API keys. Your List ID is in Audience → Settings → Audience name and defaults.',
      testable: true,
      fields: [
        {
          name: 'apiKey',
          label: 'API Key',
          type: 'password',
          placeholder: 'Your Mailchimp API key',
          required: true,
          helpText: 'Mailchimp → Account → Extras → API keys → Create A Key'
        },
        {
          name: 'listId',
          label: 'List/Audience ID',
          type: 'text',
          placeholder: 'e.g., abc123def4',
          required: true,
          helpText: 'Audience → Settings → Audience name and defaults → Audience ID'
        }
      ],
      tier: 'basic'
    },
    {
      name: 'ConvertKit',
      type: 'convertkit',
      description: 'Email marketing for course creators',
      icon: '✉️',
      instructions: 'Find your API key in ConvertKit under Settings → Advanced → API. Form ID is optional - find it in Forms → select form → URL shows form ID.',
      testable: true,
      fields: [
        {
          name: 'apiKey',
          label: 'API Secret',
          type: 'password',
          placeholder: 'Your ConvertKit API Secret',
          required: true,
          helpText: 'ConvertKit → Settings → Advanced → API Secret'
        },
        {
          name: 'formId',
          label: 'Form ID (Optional)',
          type: 'text',
          placeholder: 'e.g., 1234567',
          required: false,
          helpText: 'Forms → Click form → Check URL for form ID number'
        }
      ],
      tier: 'basic'
    },
    {
      name: 'Google Drive',
      type: 'google_drive',
      description: 'Export courses to Google Docs and Sheets',
      icon: '📁',
      instructions: 'Create OAuth credentials in Google Cloud Console. This is advanced - most users prefer using our direct HTML/PDF export instead.',
      fields: [
        {
          name: 'clientId',
          label: 'OAuth Client ID',
          type: 'text',
          placeholder: 'xxxx.apps.googleusercontent.com',
          required: true,
          helpText: 'Google Cloud Console → APIs & Services → Credentials → OAuth 2.0'
        },
        {
          name: 'folderId',
          label: 'Folder ID (Optional)',
          type: 'text',
          placeholder: 'Folder ID from URL',
          required: false,
          helpText: 'Open folder in Drive → Copy ID from URL after /folders/'
        }
      ],
      tier: 'basic'
    },
    {
      name: 'Canva',
      type: 'canva',
      description: 'Design course graphics and marketing materials',
      icon: '🎨',
      instructions: 'Canva API access requires a Canva Pro account. Get your API key from Canva Developers portal.',
      fields: [
        {
          name: 'apiKey',
          label: 'API Key',
          type: 'password',
          placeholder: 'Your Canva API key',
          required: true,
          helpText: 'developers.canva.com → Your Apps → Create App → Get API Key'
        }
      ],
      tier: 'premium'
    },
    {
      name: 'Teachable',
      type: 'teachable',
      description: 'Export courses directly to Teachable',
      icon: '🎓',
      instructions: 'Note: For now, use our Teachable CSV Export button on course pages. Direct API integration coming soon!',
      fields: [
        {
          name: 'apiKey',
          label: 'API Key',
          type: 'password',
          placeholder: 'Your Teachable API key',
          required: true,
          helpText: 'Teachable → Settings → API → Generate API Key'
        },
        {
          name: 'schoolId',
          label: 'School ID',
          type: 'text',
          placeholder: 'Your school subdomain',
          required: true,
          helpText: 'Your Teachable URL: yourschool.teachable.com'
        }
      ],
      tier: 'premium'
    },
    {
      name: 'Thinkific',
      type: 'thinkific',
      description: 'Export courses directly to Thinkific',
      icon: '📚',
      instructions: 'Note: For now, use our Thinkific JSON Export button on course pages. Direct API integration coming soon!',
      fields: [
        {
          name: 'apiKey',
          label: 'API Key',
          type: 'password',
          placeholder: 'Your Thinkific API key',
          required: true,
          helpText: 'Thinkific → Settings → Code & analytics → API'
        },
        {
          name: 'subdomain',
          label: 'Subdomain',
          type: 'text',
          placeholder: 'yourschool',
          required: true,
          helpText: 'Your Thinkific URL: yourschool.thinkific.com'
        }
      ],
      tier: 'premium'
    },
    {
      name: 'Google Analytics',
      type: 'google_analytics',
      description: 'Track course performance and user behavior',
      icon: '📊',
      instructions: 'Find your Measurement ID in Google Analytics under Admin → Data Streams → Select stream → Measurement ID.',
      testable: true,
      fields: [
        {
          name: 'trackingId',
          label: 'Measurement ID',
          type: 'text',
          placeholder: 'G-XXXXXXXXXX',
          required: true,
          helpText: 'GA4: Admin → Data Streams → Your stream → Measurement ID (starts with G-)'
        }
      ]
    },
    {
      name: 'Facebook Pixel',
      type: 'facebook_pixel',
      description: 'Track conversions and create custom audiences',
      icon: '📱',
      instructions: 'Find your Pixel ID in Meta Events Manager under Data Sources → Your Pixel.',
      testable: true,
      fields: [
        {
          name: 'pixelId',
          label: 'Pixel ID',
          type: 'text',
          placeholder: '1234567890123456',
          required: true,
          helpText: 'Meta Business Suite → Events Manager → Data Sources → Pixel ID (15-16 digits)'
        }
      ]
    },
    {
      name: 'ElevenLabs',
      type: 'elevenlabs',
      description: 'Generate realistic AI voiceovers for your course videos',
      icon: '🎙️',
      instructions: 'Get your API key from ElevenLabs dashboard. Go to your Profile → API Key. Free tier includes limited characters/month.',
      testable: true,
      tier: 'premium',
      fields: [
        {
          name: 'apiKey',
          label: 'API Key',
          type: 'password',
          placeholder: 'Your ElevenLabs API key',
          required: true,
          helpText: 'elevenlabs.io → Profile icon (top right) → API Key → Copy'
        }
      ]
    },
    {
      name: 'HeyGen',
      type: 'heygen',
      description: 'Create AI avatar videos with realistic talking heads',
      icon: '🎬',
      instructions: 'Get your API key from HeyGen. Go to Settings → API → Generate API Key. Choose an avatar in the Video Generator.',
      testable: true,
      tier: 'premium',
      fields: [
        {
          name: 'apiKey',
          label: 'API Key',
          type: 'password',
          placeholder: 'Your HeyGen API key',
          required: true,
          helpText: 'heygen.com → Settings → API → Generate API Key'
        }
      ]
    }
  ];

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchIntegrations();
  }, [user, navigate]);

  const fetchIntegrations = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/integrations`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) throw new Error('Failed to fetch integrations');

      const data = await response.json();
      setIntegrations(data);
    } catch (error) {
      console.error('Error fetching integrations:', error);
      showToast('Failed to load integrations', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleConfigure = (integration: IntegrationConfig) => {
    // Check if user's tier allows this integration
    if (integration.tier && user) {
      const userTier = (user as any).tier || 'free';
      const tierOrder = { free: 0, basic: 1, premium: 2 };
      const requiredTierLevel = tierOrder[integration.tier as keyof typeof tierOrder] || 0;
      const userTierLevel = tierOrder[userTier as keyof typeof tierOrder] || 0;

      if (userTierLevel < requiredTierLevel) {
        showToast(`This integration requires ${integration.tier} tier or higher`, 'warning');
        return;
      }
    }

    setSelectedIntegration(integration);

    // Load existing config if available
    const existing = integrations.find(i => i.integration_type === integration.type);
    if (existing) {
      setFormData(existing.config || {});
    } else {
      setFormData({});
    }

    setShowConfigModal(true);
  };

  const handleSave = async () => {
    if (!selectedIntegration) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/integrations`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            integrationType: selectedIntegration.type,
            config: formData
          })
        }
      );

      if (!response.ok) throw new Error('Failed to save integration');

      showToast(`${selectedIntegration.name} configured successfully`, 'success');
      setShowConfigModal(false);
      fetchIntegrations();
    } catch (error) {
      console.error('Error saving integration:', error);
      showToast('Failed to save integration', 'error');
    }
  };

  const handleToggle = async (id: number, isActive: boolean) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/integrations/${id}/toggle`,
        {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ isActive })
        }
      );

      if (!response.ok) throw new Error('Failed to toggle integration');

      showToast('Integration status updated', 'success');
      fetchIntegrations();
    } catch (error) {
      console.error('Error toggling integration:', error);
      showToast('Failed to update integration status', 'error');
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this integration?')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/integrations/${id}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) throw new Error('Failed to delete integration');

      showToast('Integration deleted successfully', 'success');
      fetchIntegrations();
    } catch (error) {
      console.error('Error deleting integration:', error);
      showToast('Failed to delete integration', 'error');
    }
  };

  const handleTestConnection = async () => {
    if (!selectedIntegration) return;

    setTesting(true);
    try {
      const type = selectedIntegration.type;

      // Validate required fields are filled
      const missingFields = selectedIntegration.fields
        .filter(f => f.required && !formData[f.name])
        .map(f => f.label);

      if (missingFields.length > 0) {
        showToast(`Please fill in: ${missingFields.join(', ')}`, 'error');
        setTesting(false);
        return;
      }

      // Test based on integration type
      switch (type) {
        case 'zapier': {
          // Test Zapier webhook with a test ping
          const response = await fetch(formData.webhook_url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            mode: 'no-cors', // Zapier webhooks don't return CORS headers
            body: JSON.stringify({
              test: true,
              message: 'Test connection from Course Creator Vault',
              timestamp: new Date().toISOString()
            })
          });
          // With no-cors, we can't check status, but if it doesn't throw, it sent
          showToast('Test sent to Zapier! Check your Zap to see if it received the webhook.', 'success');
          break;
        }

        case 'mailchimp': {
          // Mailchimp API key format: xxx-us12 (datacenter is after the dash)
          const apiKey = formData.apiKey;
          if (!apiKey.includes('-')) {
            showToast('Invalid API key format. Should be: xxxx-us12', 'error');
            break;
          }
          const dc = apiKey.split('-')[1];
          const response = await fetch(`https://${dc}.api.mailchimp.com/3.0/ping`, {
            headers: {
              'Authorization': `apikey ${apiKey}`
            }
          });
          if (response.ok) {
            showToast('Mailchimp connection successful!', 'success');
          } else {
            showToast('Mailchimp connection failed. Check your API key.', 'error');
          }
          break;
        }

        case 'convertkit': {
          const response = await fetch(`https://api.convertkit.com/v3/account?api_secret=${formData.apiKey}`);
          if (response.ok) {
            const data = await response.json();
            showToast(`ConvertKit connected! Account: ${data.name || 'Verified'}`, 'success');
          } else {
            showToast('ConvertKit connection failed. Check your API Secret.', 'error');
          }
          break;
        }

        case 'google_analytics': {
          // We can't fully test GA, but we can validate the format
          const trackingId = formData.trackingId;
          if (trackingId.match(/^G-[A-Z0-9]+$/)) {
            showToast('Measurement ID format looks correct! Analytics will track when users visit your pages.', 'success');
          } else {
            showToast('Invalid format. GA4 IDs should start with G- (e.g., G-ABC123XYZ)', 'error');
          }
          break;
        }

        case 'facebook_pixel': {
          // Validate Pixel ID format (15-16 digit number)
          const pixelId = formData.pixelId;
          if (pixelId.match(/^\d{15,16}$/)) {
            showToast('Pixel ID format looks correct! Pixel will track when users visit your pages.', 'success');
          } else {
            showToast('Invalid format. Pixel ID should be 15-16 digits.', 'error');
          }
          break;
        }

        case 'elevenlabs': {
          // Test ElevenLabs API by fetching available voices
          const response = await fetch('https://api.elevenlabs.io/v1/voices', {
            headers: {
              'xi-api-key': formData.apiKey
            }
          });
          if (response.ok) {
            const data = await response.json();
            const voiceCount = data.voices?.length || 0;
            showToast(`ElevenLabs connected! ${voiceCount} voices available.`, 'success');
          } else {
            showToast('ElevenLabs connection failed. Check your API key.', 'error');
          }
          break;
        }

        case 'heygen': {
          // Test HeyGen API by fetching available avatars
          const response = await fetch('https://api.heygen.com/v2/avatars', {
            headers: {
              'X-Api-Key': formData.apiKey
            }
          });
          if (response.ok) {
            const data = await response.json();
            const avatarCount = data.data?.avatars?.length || 0;
            showToast(`HeyGen connected! ${avatarCount} avatars available.`, 'success');
          } else {
            showToast('HeyGen connection failed. Check your API key.', 'error');
          }
          break;
        }

        default:
          showToast('Test not available for this integration type.', 'warning');
      }
    } catch (error: any) {
      console.error('Test connection error:', error);
      // For CORS issues with Zapier, the request might still succeed
      if (selectedIntegration.type === 'zapier') {
        showToast('Test sent! Check your Zapier dashboard to confirm receipt.', 'success');
      } else {
        showToast('Connection test failed. Please check your credentials.', 'error');
      }
    } finally {
      setTesting(false);
    }
  };

  const getIntegrationStatus = (type: string) => {
    return integrations.find(i => i.integration_type === type);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading integrations...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Integrations</h1>
          <p className="mt-2 text-gray-600">
            Connect TemplimizeAI with your favorite tools and services
          </p>
        </div>

        {/* Integrations Grid */}
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {availableIntegrations.map((integration) => {
            const status = getIntegrationStatus(integration.type);
            const isConfigured = !!status;
            const isActive = status?.is_active || false;

            return (
              <div
                key={integration.type}
                className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-start gap-4">
                    <div className="text-4xl">{integration.icon}</div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        {integration.name}
                      </h3>
                      {integration.tier && (
                        <span className="inline-block mt-1 px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-full capitalize">
                          {integration.tier}
                        </span>
                      )}
                    </div>
                  </div>
                  {isConfigured && (
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={isActive}
                        onChange={(e) => handleToggle(status.id, e.target.checked)}
                        className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                      />
                    </label>
                  )}
                </div>

                <p className="text-sm text-gray-600 mb-4">
                  {integration.description}
                </p>

                <div className="flex gap-2">
                  <button
                    onClick={() => handleConfigure(integration)}
                    className={`flex-1 px-4 py-2 rounded transition-colors text-sm ${
                      isConfigured
                        ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        : 'bg-blue-600 text-white hover:bg-blue-700'
                    }`}
                  >
                    {isConfigured ? 'Reconfigure' : 'Configure'}
                  </button>
                  {isConfigured && (
                    <button
                      onClick={() => handleDelete(status.id)}
                      className="bg-red-50 text-red-600 px-4 py-2 rounded hover:bg-red-100 transition-colors text-sm"
                    >
                      Delete
                    </button>
                  )}
                </div>

                {isConfigured && (
                  <div className="mt-3 flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${isActive ? 'bg-green-500' : 'bg-gray-400'}`}></div>
                    <span className="text-xs text-gray-600">
                      {isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Configuration Modal */}
      {showConfigModal && selectedIntegration && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center gap-3 mb-4">
              <span className="text-3xl">{selectedIntegration.icon}</span>
              <h2 className="text-2xl font-bold text-gray-900">
                Configure {selectedIntegration.name}
              </h2>
            </div>
            <p className="text-gray-600 mb-4">{selectedIntegration.description}</p>

            {/* Instructions Box */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <h4 className="font-semibold text-blue-800 text-sm mb-2 flex items-center">
                <span className="mr-2">💡</span> How to set this up:
              </h4>
              <p className="text-blue-700 text-sm">{selectedIntegration.instructions}</p>
            </div>

            <div className="space-y-5">
              {selectedIntegration.fields.map((field) => (
                <div key={field.name}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {field.label}
                    {field.required && <span className="text-red-500 ml-1">*</span>}
                  </label>
                  <input
                    type={field.type}
                    value={formData[field.name] || ''}
                    onChange={(e) =>
                      setFormData({ ...formData, [field.name]: e.target.value })
                    }
                    placeholder={field.placeholder}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required={field.required}
                  />
                  {field.helpText && (
                    <p className="mt-1 text-xs text-gray-500 flex items-start">
                      <span className="mr-1">📍</span> {field.helpText}
                    </p>
                  )}
                </div>
              ))}
            </div>

            {/* Test Connection Button */}
            {selectedIntegration.testable && (
              <div className="mt-6 pt-4 border-t border-gray-200">
                <button
                  onClick={() => handleTestConnection()}
                  disabled={testing}
                  className="w-full bg-gray-100 text-gray-700 px-4 py-3 rounded-lg hover:bg-gray-200 transition-colors font-medium flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {testing ? (
                    <>
                      <span className="animate-spin h-4 w-4 border-2 border-gray-600 border-t-transparent rounded-full"></span>
                      Testing Connection...
                    </>
                  ) : (
                    <>
                      <span>🔌</span> Test Connection
                    </>
                  )}
                </button>
              </div>
            )}

            <div className="flex gap-4 mt-6">
              <button
                onClick={handleSave}
                className="flex-1 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                Save Configuration
              </button>
              <button
                onClick={() => setShowConfigModal(false)}
                className="flex-1 bg-gray-100 text-gray-700 px-6 py-3 rounded-lg hover:bg-gray-200 transition-colors font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Integrations;
